import {
  angleFromCos,
  trigOfDegrees,
} from "@/lib/tool-kit/linear-algebra/exact-angle";
import {
  divQ,
  isRationalQ,
  mulQ,
  type QuadNumber,
  quadFromRational,
  quadToComplex,
  sqrtRational,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import { toPolar } from "@/lib/tool-kit/linear-algebra/vector";
import {
  addR,
  isZeroR,
  mulR,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Rectangular, polar, cylindrical and spherical coordinates.
 *
 * Angles come from atan2, so every quadrant is right: (−1, 1) is at 135°, not −45°.
 * Where the angles are multiples of 30° or 45° and the radius is rational the answers
 * stay exact (5√3 rather than 8.660254), and otherwise fall back to decimals.
 *
 * Spherical coordinates are stored in the ISO / physics convention: `polar` is the angle
 * from the +z axis (0°–180°) and `azimuth` the angle from +x in the xy-plane (0°–360°).
 * The calculus convention just swaps the two names; the widget does that.
 */

export interface Coord {
  /** Exact form when one exists. */
  exact: QuadNumber | null;
  value: number;
}

const decimal = (value: number): Coord => ({ exact: null, value });

function fromQuad(q: QuadNumber): Coord {
  return { exact: q, value: quadToComplex(q).re };
}

/** a × b exactly when the result stays in one Q(√d); null when two different radicals meet. */
function tryMul(a: QuadNumber, b: QuadNumber): QuadNumber | null {
  if (isRationalQ(a) || isRationalQ(b) || a.d === b.d) {
    return mulQ(a, b);
  }
  return null;
}

/** Remove the −0 and 1e−16 noise that a float cos or sin leaves at a quadrant boundary. */
function tidy(value: number): number {
  return Math.abs(value) < 1e-12 ? 0 : value;
}

export interface Angle {
  degrees: number;
  /** Whole degrees when the angle is exactly that. */
  exactDegrees: number | null;
}

/* ---------------------------------------------------------- 2D conversions */

export interface RectToPolar {
  /** Undefined angle at the origin. */
  angle: Angle | null;
  radius: QuadNumber;
  radiusValue: number;
  quadrant: 0 | 1 | 2 | 3 | 4;
  /** The same angle in (−180°, 180°]. */
  signedDegrees: number | null;
  /** The reference angle (acute, to the nearest x-axis) in degrees. */
  referenceDegrees: number | null;
}

export function rectToPolar(x: Rational, y: Rational): RectToPolar {
  const polar = toPolar(x, y);
  if (polar === null) {
    return {
      angle: null,
      radius: quadFromRational(mulR(x, x)),
      radiusValue: 0,
      quadrant: 0,
      signedDegrees: null,
      referenceDegrees: null,
    };
  }
  const signed = polar.degrees > 180 ? polar.degrees - 360 : polar.degrees;
  const xn = toNumberR(x);
  const yn = toNumberR(y);
  const reference = (Math.atan2(Math.abs(yn), Math.abs(xn)) * 180) / Math.PI;
  return {
    angle: { degrees: polar.degrees, exactDegrees: polar.exactDegrees },
    radius: polar.magnitude,
    radiusValue: quadToComplex(polar.magnitude).re,
    quadrant: polar.quadrant,
    signedDegrees: signed,
    referenceDegrees: reference,
  };
}

export interface PolarToRect {
  x: Coord;
  y: Coord;
}

export function polarToRect(r: Rational, angle: Angle): PolarToRect {
  const trig =
    angle.exactDegrees === null ? null : trigOfDegrees(angle.exactDegrees);
  if (trig) {
    const scale = quadFromRational(r);
    return {
      x: fromQuad(mulQ(scale, trig.cos)),
      y: fromQuad(mulQ(scale, trig.sin)),
    };
  }
  const radians = (angle.degrees * Math.PI) / 180;
  const rn = toNumberR(r);
  return {
    x: decimal(tidy(rn * Math.cos(radians))),
    y: decimal(tidy(rn * Math.sin(radians))),
  };
}

/* ---------------------------------------------------------- 3D conversions */

export interface RectTo3D {
  /** ρ = √(x² + y²), the distance from the z-axis. */
  rho: QuadNumber;
  rhoValue: number;
  /** r = √(x² + y² + z²), the distance from the origin. */
  radius: QuadNumber;
  radiusValue: number;
  /** Angle from +x in the xy-plane; null on the z-axis, where it is undefined. */
  azimuth: Angle | null;
  /** Angle from +z; null at the origin. */
  polar: Angle | null;
}

export function rectTo3D(x: Rational, y: Rational, z: Rational): RectTo3D {
  const rhoSquared = addR(mulR(x, x), mulR(y, y));
  const rho = sqrtRational(rhoSquared);
  const radius = sqrtRational(addR(rhoSquared, mulR(z, z)));
  const rhoValue = quadToComplex(rho).re;
  const radiusValue = quadToComplex(radius).re;
  const flat = toPolar(x, y);
  let polar: Angle | null = null;
  if (!(isZeroR(radius.a) && isZeroR(radius.b))) {
    const cos = divQ(quadFromRational(z), radius);
    polar = {
      degrees: (Math.atan2(rhoValue, toNumberR(z)) * 180) / Math.PI,
      exactDegrees: angleFromCos(cos),
    };
  }
  return {
    rho,
    rhoValue,
    radius,
    radiusValue,
    azimuth: flat
      ? { degrees: flat.degrees, exactDegrees: flat.exactDegrees }
      : null,
    polar,
  };
}

export interface Rect3D {
  x: Coord;
  y: Coord;
  z: Coord;
}

/** Cylindrical (ρ, azimuth φ, z) to rectangular. */
export function cylindricalToRect(
  rho: Rational,
  azimuth: Angle,
  z: Rational
): Rect3D {
  const flat = polarToRect(rho, azimuth);
  return { x: flat.x, y: flat.y, z: fromQuad(quadFromRational(z)) };
}

function exactSphericalPart(
  r: QuadNumber,
  a: QuadNumber,
  b: QuadNumber
): Coord | null {
  const partial = tryMul(a, b);
  const total = partial ? tryMul(r, partial) : null;
  return total ? fromQuad(total) : null;
}

/** Spherical (r, polar θ from +z, azimuth φ from +x) to rectangular. */
export function sphericalToRect(
  r: Rational,
  polar: Angle,
  azimuth: Angle
): Rect3D {
  const rq = quadFromRational(r);
  const tp =
    polar.exactDegrees === null ? null : trigOfDegrees(polar.exactDegrees);
  const ta =
    azimuth.exactDegrees === null ? null : trigOfDegrees(azimuth.exactDegrees);
  const rn = toNumberR(r);
  const p = (polar.degrees * Math.PI) / 180;
  const a = (azimuth.degrees * Math.PI) / 180;
  const numeric = {
    x: decimal(tidy(rn * Math.sin(p) * Math.cos(a))),
    y: decimal(tidy(rn * Math.sin(p) * Math.sin(a))),
    z: decimal(tidy(rn * Math.cos(p))),
  };
  if (!tp) {
    return numeric;
  }
  const z = fromQuad(mulQ(rq, tp.cos));
  if (!ta) {
    return { ...numeric, z };
  }
  return {
    x: exactSphericalPart(rq, tp.sin, ta.cos) ?? numeric.x,
    y: exactSphericalPart(rq, tp.sin, ta.sin) ?? numeric.y,
    z,
  };
}
